import { type AxiosRequestConfig } from 'axios';
import TripPackageLoader, {
    type TripPackageLoaderResult,
} from '../TripPackageLoader';
import BusPackage from './BusPackage';
import { type BusPackageLoaderConfig } from './types';
import {
    type GetServiceInputDto,
    GetServicesApiApiGetBusServicesPost,
} from '@clients/BusApi';
import { ReserveType } from '@utils/itinerary';

export class BusPackageLoader extends TripPackageLoader<BusPackage> {
    private readonly _specificProviderId: number | null;
    private readonly _originTerminalCode: string;
    private readonly _destinationTerminalCode: string;
    constructor({
        originCode,
        destinationCode,
        originTerminalId,
        destinationTerminalId,
        date,
        specificProviderId,
        url,
        originName,
        destinationName,
        totalSeatCount,
        isRoundTrip,
    }: BusPackageLoaderConfig) {
        super({
            totalSeatCount,
            destinationCode: destinationTerminalId.toString(),
            destinationName,
            originCode: originTerminalId.toString(),
            originName,
            date,
            url,
            isRoundTrip,
        });
        this._specificProviderId = specificProviderId;
        this._originTerminalCode = originCode;
        this._destinationTerminalCode = destinationCode;
    }

    get totalSeatCount() {
        return this._totalSeatCount;
    }

    reserveType = ReserveType.Bus;

    get adultCount() {
        return this._totalSeatCount;
    }

    get childCount() {
        return 0;
    }

    get infantCount() {
        return 0;
    }

    get originTerminalCode(): string {
        return this._originTerminalCode;
    }

    get destinationTerminalCode(): string {
        return this._destinationTerminalCode;
    }

    override compareTo(other: BusPackageLoaderConfig): boolean {
        if (this._originCode !== other.originCode) {
            return false;
        }
        if (this._destinationCode !== other.destinationCode) {
            return false;
        }
        if (this.date.toString() !== other.date.toString()) {
            return false;
        }
        if (this._totalSeatCount !== other.totalSeatCount) {
            return false;
        }
        // noinspection RedundantIfStatementJS
        if (this._specificProviderId !== other.specificProviderId) {
            return false;
        }
        return true;
    }

    private provider(axiosParams?: AxiosRequestConfig<any>) {
        const getServiceInputDto = {
            from: Number(this._originCode),
            to: Number(this._destinationCode),
            date: dayConfig.gregory(this.date).format(formatter()),
            includeClosed: true,
            includePromotions: true,
            loadFromDbOnUnavailability: true,
            includeUnderDevelopment: true,
        } satisfies GetServiceInputDto;
        return GetServicesApiApiGetBusServicesPost(
            {
                getServiceInputDto,
            },
            axiosParams
        );
    }

    override async *_getPackages(
        axiosParams?: AxiosRequestConfig<any>
    ): AsyncGenerator<TripPackageLoaderResult<BusPackage>> {
        const { data } = await this.provider(axiosParams);

        const packages = data.buses?.map(
            bus =>
                new BusPackage(
                    bus,
                    this._totalSeatCount,
                    false,
                    ReserveType.Bus
                )
        );

        yield {
            list: packages ?? [],
            originLocation: data.fromLocation
                ? {
                      latitude: data.fromLocation.latitude ?? 0,
                      longitude: data.fromLocation.longitude ?? 0,
                      title: data.fromLocation?.title,
                  }
                : null,
            destinationLocation: data.toLocation
                ? {
                      latitude: data.toLocation.latitude ?? 0,
                      longitude: data.toLocation.longitude ?? 0,
                      title: data.toLocation?.title,
                  }
                : null,
            filterMetaData: data.filterData ?? {},
        };
    }
}
